import RequestStatus from "../model/RequestStatus";
import { DecisionTableName, DecisionTableConfig, CreateDecisionTableConfigRequest, UpdateDecisionTableConfigRequest } from "src/model/DecisionTableConfig";

// Decision Table Names Actions
export const FETCH_DECISION_TABLE_NAMES_START = 'FETCH_DECISION_TABLE_NAMES_START';
export const FETCH_DECISION_TABLE_NAMES_SUCCESS = 'FETCH_DECISION_TABLE_NAMES_SUCCESS';
export const FETCH_DECISION_TABLE_NAMES_FAILED = 'FETCH_DECISION_TABLE_NAMES_FAILED';

// Fee Configuration Query Actions
export const FETCH_DECISION_TABLE_CONFIG_START = 'FETCH_DECISION_TABLE_CONFIG_START';
export const FETCH_DECISION_TABLE_CONFIG_SUCCESS = 'FETCH_DECISION_TABLE_CONFIG_SUCCESS';
export const FETCH_DECISION_TABLE_CONFIG_FAILED = 'FETCH_DECISION_TABLE_CONFIG_FAILED';

// Fee Configuration Save Actions
export const SAVE_DECISION_TABLE_CONFIG_START = 'SAVE_DECISION_TABLE_CONFIG_START';
export const SAVE_DECISION_TABLE_CONFIG_SUCCESS = 'SAVE_DECISION_TABLE_CONFIG_SUCCESS';
export const SAVE_DECISION_TABLE_CONFIG_FAILED = 'SAVE_DECISION_TABLE_CONFIG_FAILED';

// Fee Configuration Create Actions
export const CREATE_DECISION_TABLE_CONFIG_START = 'CREATE_DECISION_TABLE_CONFIG_START';
export const CREATE_DECISION_TABLE_CONFIG_SUCCESS = 'CREATE_DECISION_TABLE_CONFIG_SUCCESS';
export const CREATE_DECISION_TABLE_CONFIG_FAILED = 'CREATE_DECISION_TABLE_CONFIG_FAILED';

// Fee Configuration Delete Actions
export const DELETE_DECISION_TABLE_CONFIG_START = 'DELETE_DECISION_TABLE_CONFIG_START';
export const DELETE_DECISION_TABLE_CONFIG_SUCCESS = 'DELETE_DECISION_TABLE_CONFIG_SUCCESS';
export const DELETE_DECISION_TABLE_CONFIG_FAILED = 'DELETE_DECISION_TABLE_CONFIG_FAILED';
export const RESET_DELETE_DECISION_TABLE_CONFIG_STATE = 'RESET_DELETE_DECISION_TABLE_CONFIG_STATE';

// Reset Actions for Save and Create
export const RESET_SAVE_DECISION_TABLE_CONFIG_STATE = 'RESET_SAVE_DECISION_TABLE_CONFIG_STATE';
export const RESET_CREATE_DECISION_TABLE_CONFIG_STATE = 'RESET_CREATE_DECISION_TABLE_CONFIG_STATE';

// Decision Table Names Action Interfaces
export interface FetchDecisionTableNamesStartAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
}

export interface FetchDecisionTableNamesCompleteAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly data: DecisionTableName[] | null;
}

// Fee Configuration Query Action Interfaces
export interface FetchDecisionTableConfigStartAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly decisionTableName: string;
}

export interface FetchDecisionTableConfigCompleteAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly data: DecisionTableConfig | null;
}

// Fee Configuration Save Action Interfaces
export interface SaveDecisionTableConfigStartAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly request: UpdateDecisionTableConfigRequest;
}

export interface SaveDecisionTableConfigCompleteAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly data: DecisionTableConfig | null;
    readonly error?: string;
}

// Fee Configuration Create Action Interfaces
export interface CreateDecisionTableConfigStartAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly request: CreateDecisionTableConfigRequest;
}

export interface CreateDecisionTableConfigCompleteAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly data: DecisionTableConfig | null;
    readonly error?: string;
}

// Fee Configuration Delete Action Interfaces
export interface DeleteDecisionTableConfigStartAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly decisionTableName: string;
}

export interface DeleteDecisionTableConfigCompleteAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
    readonly error?: string;
}

export interface ResetDeleteDecisionTableConfigStateAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
}

// Reset Action Interfaces for Save and Create
export interface ResetSaveDecisionTableConfigStateAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
}

export interface ResetCreateDecisionTableConfigStateAction {
    readonly type: string;
    readonly requestStatus: RequestStatus;
}

// Decision Table Names Action Creators
export const fetchDecisionTableNamesStart = (): FetchDecisionTableNamesStartAction => ({
    type: FETCH_DECISION_TABLE_NAMES_START,
    requestStatus: RequestStatus.LOADING,
});

export const fetchDecisionTableNamesSuccess = (data: DecisionTableName[]): FetchDecisionTableNamesCompleteAction => ({
    type: FETCH_DECISION_TABLE_NAMES_SUCCESS,
    requestStatus: RequestStatus.SUCCESS,
    data: data
});

export const fetchDecisionTableNamesFailed = (): FetchDecisionTableNamesCompleteAction => ({
    type: FETCH_DECISION_TABLE_NAMES_FAILED,
    requestStatus: RequestStatus.ERROR,
    data: null
});

// Fee Configuration Query Action Creators
export const fetchDecisionTableConfigStart = (decisionTableName: string): FetchDecisionTableConfigStartAction => ({
    type: FETCH_DECISION_TABLE_CONFIG_START,
    requestStatus: RequestStatus.LOADING,
    decisionTableName: decisionTableName
});

export const fetchDecisionTableConfigSuccess = (data: DecisionTableConfig): FetchDecisionTableConfigCompleteAction => ({
    type: FETCH_DECISION_TABLE_CONFIG_SUCCESS,
    requestStatus: RequestStatus.SUCCESS,
    data: data
});

export const fetchDecisionTableConfigFailed = (): FetchDecisionTableConfigCompleteAction => ({
    type: FETCH_DECISION_TABLE_CONFIG_FAILED,
    requestStatus: RequestStatus.ERROR,
    data: null
});

// Fee Configuration Save Action Creators
export const saveDecisionTableConfigStart = (request: UpdateDecisionTableConfigRequest): SaveDecisionTableConfigStartAction => ({
    type: SAVE_DECISION_TABLE_CONFIG_START,
    requestStatus: RequestStatus.UPDATING,
    request: request
});

export const saveDecisionTableConfigSuccess = (data: DecisionTableConfig): SaveDecisionTableConfigCompleteAction => ({
    type: SAVE_DECISION_TABLE_CONFIG_SUCCESS,
    requestStatus: RequestStatus.SUCCESS,
    data: data
});

export const saveDecisionTableConfigFailed = (error: string): SaveDecisionTableConfigCompleteAction => ({
    type: SAVE_DECISION_TABLE_CONFIG_FAILED,
    requestStatus: RequestStatus.ERROR,
    data: null,
    error: error
});

// Fee Configuration Create Action Creators
export const createDecisionTableConfigStart = (request: CreateDecisionTableConfigRequest): CreateDecisionTableConfigStartAction => ({
    type: CREATE_DECISION_TABLE_CONFIG_START,
    requestStatus: RequestStatus.UPDATING,
    request: request
});

export const createDecisionTableConfigSuccess = (data: DecisionTableConfig): CreateDecisionTableConfigCompleteAction => ({
    type: CREATE_DECISION_TABLE_CONFIG_SUCCESS,
    requestStatus: RequestStatus.SUCCESS,
    data: data
});

export const createDecisionTableConfigFailed = (error: string): CreateDecisionTableConfigCompleteAction => ({
    type: CREATE_DECISION_TABLE_CONFIG_FAILED,
    requestStatus: RequestStatus.ERROR,
    data: null,
    error: error
});

// Fee Configuration Delete Action Creators
export const deleteDecisionTableConfigStart = (decisionTableName: string): DeleteDecisionTableConfigStartAction => ({
    type: DELETE_DECISION_TABLE_CONFIG_START,
    requestStatus: RequestStatus.UPDATING,
    decisionTableName: decisionTableName
});

export const deleteDecisionTableConfigSuccess = (): DeleteDecisionTableConfigCompleteAction => ({
    type: DELETE_DECISION_TABLE_CONFIG_SUCCESS,
    requestStatus: RequestStatus.SUCCESS,
});

export const deleteDecisionTableConfigFailed = (error: string): DeleteDecisionTableConfigCompleteAction => ({
    type: DELETE_DECISION_TABLE_CONFIG_FAILED,
    requestStatus: RequestStatus.ERROR,
    error: error
});

export const resetDeleteDecisionTableConfigState = (): ResetDeleteDecisionTableConfigStateAction => ({
    type: RESET_DELETE_DECISION_TABLE_CONFIG_STATE,
    requestStatus: RequestStatus.NOT_LOADED,
});

// Reset Action Creators for Save and Create
export const resetSaveDecisionTableConfigState = (): ResetSaveDecisionTableConfigStateAction => ({
    type: RESET_SAVE_DECISION_TABLE_CONFIG_STATE,
    requestStatus: RequestStatus.NOT_LOADED,
});

export const resetCreateDecisionTableConfigState = (): ResetCreateDecisionTableConfigStateAction => ({
    type: RESET_CREATE_DECISION_TABLE_CONFIG_STATE,
    requestStatus: RequestStatus.NOT_LOADED,
});
